import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassBadge,
  GlassButton,
  GlassInput,
  GlassSelect,
  GlassPageHeader,
  GlassTabs,
  MetricCard,
  SectionReveal,
  CollapsibleMetricsSection,
} from '@/design-system';
import { Settings, Users, Target, TrendingUp, Layers, Building } from 'lucide-react';
import type { Column } from '@/types';
import { formatPercent } from '@/utils/formatters';

interface Epic {
  id: string;
  name: string;
  status: string;
  priority?: number;
  requestedTeamWeeks: number;
  allocatedTeamWeeks: number;
  utilizationPercentage: number;
  investibleStructure: {
    id: string;
    name: string;
    category: string;
    owner: string;
  };
  primaryProgram?: {
    id: string;
    name: string;
  };
  contributingPrograms: Array<{ id: string; name: string }>;
  featureCount: number;
  readyFeatures: number;
  readinessPercentage: number;
}

interface UsersByRole {
  [role: string]: Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isActive: boolean;
    ownedResources: {
      investibleStructures: number;
      programs: number;
      teams: number;
    };
  }>;
}

interface AdminMetrics {
  portfolio: {
    totalEpics: number;
    committedEpics: number;
    totalFeatures: number;
    shovelReadyFeatures: number;
    totalPrograms: number;
    totalTeams: number;
    totalUsers: number;
  };
  capacity: {
    totalRequested: number;
    totalAllocated: number;
    utilizationPercentage: number;
  };
  readiness: {
    totalFeatures: number;
    shovelReadyFeatures: number;
    readinessPercentage: number;
  };
}

const AdminDashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'epics' | 'users' | 'metrics'>('epics');
  const [epics, setEpics] = useState<Epic[]>([]);
  const [usersByRole, setUsersByRole] = useState<UsersByRole>({});
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  useEffect(() => {
    if (activeTab === 'epics') {
      fetchEpics();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'metrics') {
      fetchMetrics();
    }
  }, [activeTab, statusFilter]);

  const fetchEpics = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`/api/v1/admin-dashboard/epics?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setEpics(data.data.epics);
      }
    } catch (error) {
      console.error('Failed to fetch EPICs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedRole) params.append('role', selectedRole);

      const response = await fetch(`/api/v1/admin-dashboard/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setUsersByRole(data.data.usersByRole);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMetrics = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/admin-dashboard/metrics', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setMetrics(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'; label: string }> = {
      DRAFT: { variant: 'default', label: 'Draft' },
      SUBMITTED: { variant: 'info', label: 'Submitted' },
      PRIORITIZED: { variant: 'warning', label: 'Prioritized' },
      COMMITTED: { variant: 'success', label: 'Committed' },
      STRETCH: { variant: 'warning', label: 'Stretch' },
      DELIVERED: { variant: 'success', label: 'Delivered' },
      CANCELLED: { variant: 'danger', label: 'Cancelled' },
    };

    const config = statusConfig[status] || statusConfig.DRAFT;
    return <GlassBadge variant={config.variant}>{config.label}</GlassBadge>;
  };

  const epicColumns: Column<Epic>[] = [
    {
      key: 'priority',
      header: 'P',
      sortable: true,
      render: (value) => value ? <span className="font-mono text-xs">{value}</span> : '-',
    },
    {
      key: 'name',
      header: 'EPIC',
      sortable: true,
      render: (value, row) => (
        <div className="min-w-[200px]">
          <div className="font-medium text-foreground text-sm">{value}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{row.investibleStructure.name}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      filterable: true,
      render: (value) => getStatusBadge(value),
    },
    {
      key: 'capacity',
      header: 'Capacity',
      render: (_, row) => (
        <div className="text-xs">
          <div className="text-foreground font-medium">
            {row.allocatedTeamWeeks} / {row.requestedTeamWeeks} TW
          </div>
          <div className="text-muted-foreground">
            {formatPercent(row.utilizationPercentage, { decimals: 0 })}
          </div>
        </div>
      ),
    },
    {
      key: 'readiness',
      header: 'Readiness',
      render: (_, row) => (
        <div className="text-xs">
          <div className="text-foreground font-medium">
            {row.readyFeatures} / {row.featureCount}
          </div>
          <div className="text-muted-foreground">
            {formatPercent(row.readinessPercentage, { decimals: 0 })}
          </div>
        </div>
      ),
    },
    {
      key: 'primaryProgram',
      header: 'Program',
      render: (value) => value ? (
        <span className="text-xs text-foreground">{value.name}</span>
      ) : (
        <span className="text-xs text-muted-foreground">-</span>
      ),
    },
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'PRIORITIZED', label: 'Prioritized' },
    { value: 'COMMITTED', label: 'Committed' },
    { value: 'STRETCH', label: 'Stretch' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'EXECUTIVE', label: 'Executive' },
    { value: 'IS_OWNER', label: 'IS Owner' },
    { value: 'PROGRAM_LEAD', label: 'Program Lead' },
    { value: 'PMO', label: 'PMO' },
    { value: 'FINANCE', label: 'Finance' },
    { value: 'VIEWER', label: 'Viewer' },
  ];

  const tabItems = [
    { id: 'epics', label: 'All EPICs' },
    { id: 'users', label: 'Users by Role' },
    { id: 'metrics', label: 'Portfolio Metrics' },
  ];

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Admin Dashboard"
          subtitle="System-wide overview and user management"
          icon={<Settings className="w-6 h-6" />}
          iconGradient="blue-violet"
          className="mb-6"
        />
      </SectionReveal>

      {/* Tabs */}
      <SectionReveal delay={0.1}>
        <GlassTabs
          tabs={tabItems}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as 'epics' | 'users' | 'metrics')}
          className="mb-6"
        />
      </SectionReveal>

      {/* EPICs Tab */}
      {activeTab === 'epics' && (
        <SectionReveal delay={0.15}>
          <GlassCard padding="none">
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <GlassInput
                    placeholder="Search EPICs..."
                    value={searchTerm}
                    onChange={(value) => setSearchTerm(value)}
                  />
                </div>
                <GlassSelect
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value)}
                  options={statusOptions}
                />
                <GlassButton variant="primary" onClick={fetchEpics}>
                  Search
                </GlassButton>
              </div>
            </div>

            <DataTable
              data={epics}
              columns={epicColumns}
              loading={isLoading}
              emptyMessage="No EPICs found"
            />
          </GlassCard>
        </SectionReveal>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <SectionReveal delay={0.15}>
          <div className="space-y-4">
            <GlassCard>
              <div className="flex items-center gap-4">
                <GlassSelect
                  value={selectedRole}
                  onChange={(value) => {
                    setSelectedRole(value);
                    fetchUsers();
                  }}
                  options={roleOptions}
                />
              </div>
            </GlassCard>

            {Object.entries(usersByRole).map(([role, users]) => (
              <GlassCard key={role}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[var(--text)]">{role.replace('_', ' ')}</h3>
                  <GlassBadge variant="info">{users.length} users</GlassBadge>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead>
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-[var(--muted)]">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-[var(--muted)]">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-[var(--muted)]">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-[var(--muted)]">Owned Resources</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-white/5">
                          <td className="px-3 py-2 text-sm text-[var(--text)]">
                            {user.firstName} {user.lastName}
                          </td>
                          <td className="px-3 py-2 text-sm text-[var(--text)]">{user.email}</td>
                          <td className="px-3 py-2">
                            <GlassBadge variant={user.isActive ? 'success' : 'default'}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </GlassBadge>
                          </td>
                          <td className="px-3 py-2 text-xs text-[var(--muted)]">
                            {user.ownedResources.investibleStructures > 0 && (
                              <span className="mr-2">{user.ownedResources.investibleStructures} IS</span>
                            )}
                            {user.ownedResources.programs > 0 && (
                              <span className="mr-2">{user.ownedResources.programs} Programs</span>
                            )}
                            {user.ownedResources.teams > 0 && (
                              <span>{user.ownedResources.teams} Teams</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            ))}
          </div>
        </SectionReveal>
      )}

      {/* Metrics Tab */}
      {activeTab === 'metrics' && metrics && (
        <SectionReveal delay={0.15}>
          <CollapsibleMetricsSection storageKey="admin-dashboard">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <MetricCard
                title="Total EPICs"
                value={metrics.portfolio.totalEpics}
                icon={<Layers className="w-5 h-5" />}
                iconColor="blue"
              />
              <MetricCard
                title="Committed EPICs"
                value={metrics.portfolio.committedEpics}
                icon={<Target className="w-5 h-5" />}
                iconColor="emerald"
              />
              <MetricCard
                title="Total Features"
                value={metrics.portfolio.totalFeatures}
                icon={<Layers className="w-5 h-5" />}
                iconColor="violet"
              />
              <MetricCard
                title="Shovel Ready"
                value={metrics.portfolio.shovelReadyFeatures}
                icon={<Target className="w-5 h-5" />}
                iconColor="cyan"
              />
              <MetricCard
                title="Programs"
                value={metrics.portfolio.totalPrograms}
                icon={<Building className="w-5 h-5" />}
                iconColor="amber"
              />
              <MetricCard
                title="Users"
                value={metrics.portfolio.totalUsers}
                icon={<Users className="w-5 h-5" />}
                iconColor="rose"
              />
            </div>
          </CollapsibleMetricsSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <GlassCard>
              <h3 className="text-sm font-medium text-[var(--muted)] mb-4">Portfolio Overview</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-[var(--text)]">Total EPICs</span>
                  <span className="text-[var(--text)] font-semibold">{metrics.portfolio.totalEpics}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text)]">Committed EPICs</span>
                  <span className="text-status-success font-semibold">{metrics.portfolio.committedEpics}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text)]">Total Features</span>
                  <span className="text-[var(--text)] font-semibold">{metrics.portfolio.totalFeatures}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text)]">Shovel Ready</span>
                  <span className="text-status-success font-semibold">{metrics.portfolio.shovelReadyFeatures}</span>
                </div>
              </div>
            </GlassCard>

            <GlassCard>
              <h3 className="text-sm font-medium text-[var(--muted)] mb-4">Capacity</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-[var(--text)]">Requested</span>
                  <span className="text-[var(--text)] font-semibold">{metrics.capacity.totalRequested} TW</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text)]">Allocated</span>
                  <span className="text-accent-blue font-semibold">{metrics.capacity.totalAllocated} TW</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text)]">Utilization</span>
                  <span className={`font-semibold ${
                    metrics.capacity.utilizationPercentage > 100 ? 'text-status-error' :
                    metrics.capacity.utilizationPercentage > 90 ? 'text-status-warning' : 'text-status-success'
                  }`}>
                    {formatPercent(metrics.capacity.utilizationPercentage, { decimals: 0 })}
                  </span>
                </div>
              </div>
            </GlassCard>

            <GlassCard>
              <h3 className="text-sm font-medium text-[var(--muted)] mb-4">Resources</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-[var(--text)]">Programs</span>
                  <span className="text-[var(--text)] font-semibold">{metrics.portfolio.totalPrograms}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text)]">Teams</span>
                  <span className="text-[var(--text)] font-semibold">{metrics.portfolio.totalTeams}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text)]">Users</span>
                  <span className="text-[var(--text)] font-semibold">{metrics.portfolio.totalUsers}</span>
                </div>
              </div>
            </GlassCard>
          </div>
        </SectionReveal>
      )}
    </div>
  );
};

export default AdminDashboardPage;
